import { Dropdown } from "Common/Components/Dropdown";
import { useAppServices } from "hook/services";
import { Eye, MoreHorizontal, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";

export default function ContactsData() {
  const AppService = useAppServices();
  const { locationId } = useParams();
  const [contacts, setContacts] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  
  // Server-side pagination and filtering states
  const [pagination, setPagination] = useState({
    page: 1, // Backend uses 1-based page indexing
    limit: 10,
    total: 0,
    pages: 0,
    sortField: "createdAt",
    sortOrder: "desc"
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  // Prevent double calls in development
  const hasFetched = useRef(false);

  const buildQueryString = () => {
    const params = new URLSearchParams();
    
    // Required location filter
    if (locationId) {
      params.append("locationId", locationId);
    }
    
    // Pagination parameters
    params.append("page", pagination.page.toString());
    params.append("limit", pagination.limit.toString());
    
    // Search parameter
    if (searchTerm) {
      params.append("search", searchTerm);
    }
    
    // Sorting parameters
    if (pagination.sortField && pagination.sortOrder) {
      params.append("sortBy", pagination.sortField);
      params.append("sortOrder", pagination.sortOrder);
    }
    
    return params.toString();
  };

  const getContacts = async () => {
    setLoading(true);
    try {
      const query = buildQueryString();
      const { response, error } = await AppService.contact.filter({
        query: query,
      });
      
      if (response && response.data) {
        setContacts(response.data);
        
        // Update pagination info from API response
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.pagination.total,
            pages: response.pagination.pages
          }));
        }
      } else {
        setContacts([]);
        setPagination(prev => ({
          ...prev,
          total: 0,
          pages: 0
        }));
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data when dependencies change
  useEffect(() => {
    // Prevent double calls in development (Strict Mode)
    if (hasFetched.current) {
      getContacts();
    } else {
      hasFetched.current = true;
    }
  }, [pagination.page, pagination.limit, searchTerm, pagination.sortField, pagination.sortOrder]);

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(contacts.map((acc: any) => acc._id));
    } else {
      setSelectedRows([]);
    }
  };

  const toggleSelectRow = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, id]);
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== id));
    }
  };

  const handleDelete = async (_id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const { response, error } = await AppService.contact.delete({
          query: `_id=${_id}`,
        });

        if (response) {
          Swal.fire("Deleted!", "The record has been deleted.", "success");
          getContacts(); // Refresh data
        } else {
          Swal.fire(
            "Error!",
            error?.message || "Something went wrong.",
            "error"
          );
        }
      } catch (err: any) {
        Swal.fire("Error!", err.message || "Delete failed.", "error");
      }
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on search
  };

  // Handle page change (1-based)
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Handle page size change
  const handlePageSizeChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  // Handle sorting
  const handleSort = (field: string, direction: string) => {
    setPagination(prev => ({ 
      ...prev, 
      sortField: field, 
      sortOrder: direction,
      page: 1 // Reset to first page when sorting
    }));
  };

  const columns = useMemo(
    () => [
      {
        header: (() => {
          const allSelected =
            selectedRows.length === contacts.length && contacts.length > 0;

          if (allSelected) {
            return (
              <input
                id="CheckboxAll"
                className="size-4 cursor-pointer"
                type="checkbox"
                checked
                onChange={(e) => toggleSelectAll(e.target.checked)}
              />
            );
          }
          return (
            <input
              id="CheckboxAll"
              className="size-4 cursor-pointer"
              type="checkbox"
              onChange={(e) => toggleSelectAll(e.target.checked)}
            />
          );
        })(),
        id: "checkAll",
        cell: (cell: any) => {
          const rowId = cell.row.original._id;
          if (selectedRows.includes(rowId))
            return (
              <input
                className="size-4 cursor-pointer"
                type="checkbox"
                checked
                onChange={(e) => toggleSelectRow(rowId, e.target.checked)}
              />
            );
          return (
            <input
              className="size-4 cursor-pointer"
              type="checkbox"
              onChange={(e) => toggleSelectRow(rowId, e.target.checked)}
            />
          );
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        enableSorting: true,
        cell: (cell: any) => {
          const row = cell.row.original;

          // ✅ Use "name" if present, otherwise combine firstName + lastName
          const fullName =
            row.name?.trim() ||
            [row.firstName, row.lastName].filter(Boolean).join(" ") ||
            "—"; // fallback if both missing

          return (
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center size-10 font-medium rounded-full shrink-0 bg-slate-200 text-slate-800 dark:text-zink-50 dark:bg-zink-600">
                {row.img ? (
                  <img
                    src={row.img}
                    alt={fullName}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  fullName
                    .split(" ")
                    .map((word: string) => word.charAt(0).toUpperCase())
                    .join("")
                )}
              </div>

              <div className="grow">
                <h6 className="mb-1">
                  <Link to="#!" className="name">
                    {fullName}
                  </Link>
                </h6>
                {row.designation && (
                  <p className="text-slate-500 dark:text-zink-200">
                    {row.designation}
                  </p>
                )}
              </div>
            </div>
          );
        },
      },
      {
        header: "Email",
        accessorKey: "email",
        enableSorting: true,
      },
      {
        header: "Phone Number",
        accessorKey: "phone",
        enableSorting: true,
      },
      {
        header: "Action",
        cell: (cell: any) => (
          <Dropdown className="relative">
            <Dropdown.Trigger
              className="flex items-center justify-center size-[30px] p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20"
              id="usersAction1"
            >
              <MoreHorizontal className="size-3" />
            </Dropdown.Trigger>
            <Dropdown.Content
              placement="right-end"
              className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md min-w-[10rem] dark:bg-zink-600"
              aria-labelledby="usersAction1"
            >
              <li>
                <Link to={`./detail/${cell.row.original._id}`}>
                  <div className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200 cursor-pointer">
                    <Eye className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                    <span className="align-middle">Overview</span>
                  </div>
                </Link>
              </li>
              <li>
                <div
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200 cursor-pointer"
                  onClick={() => handleDelete(cell.row.original._id)}
                >
                  <Trash2 className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Delete</span>
                </div>
              </li>
            </Dropdown.Content>
          </Dropdown>
        ),
      },
    ],
    [selectedRows, contacts]
  );

  return {
    loading: loading,
    contacts: contacts,
    selectedRows: selectedRows,
    pagination: pagination,
    searchTerm: searchTerm,
    resetSelectedRows: () => setSelectedRows([]),
    columns: columns,
    handleRefresh: getContacts,
    handleSearch: handleSearch,
    handlePageChange: handlePageChange,
    handlePageSizeChange: handlePageSizeChange,
    handleSort: handleSort,
  };
}